import { ControlType, IEntity, Property, PropertyType } from "../../../base/types";
import { FC, useEffect, useState } from "react";
import { IControlAttributes } from "../../../base/models";
import { Registry } from "../../../base/register";
import { useGetProperties } from "../../../base/hooks";
import { ICourseHistory, ICourseType } from "./properties/IProgramStructure";
import { useGetCourseHistoryByStudentIdGradeQuery, useGetCourseHistoryByStudentIdQuery } from "./properties/courseHistoryApi";
import { useAuth } from "../../../../modules/auth";
import { useCreateMutation, useDeleteMutation } from "../../../services/data/tableDataApi";
import { IFieldValue } from "../../../services/models";
import { useGetCoursesName } from "../take-home-examination/properties/useGetCoursesName";
import { useGetSemesterRegistrationQuery } from "./properties/programStructureApi";

export const RemedialRegComponent: FC<IControlAttributes> = ({
    control,
    className,
    children,
    props,
    designerMode,
    ...rest
}) => {
    const { properties } = useGetProperties({ bindings: control.bindings });
    const registrationType: number | undefined = properties[Property.RegistrationType];
    const academicSession: string | undefined = properties[Property.AcademicSession];
    const IsRemedial: boolean | undefined = properties[Property.IsRemedial];
    const isChecked: boolean | undefined = properties[Property.IsChecked];
    const readOnly: boolean | undefined = properties[Property.Readonly];
    const disabled: boolean | undefined = properties[Property.Disabled];
    const [, setError] = useState<string>('');
    const { user, siteId } = useAuth();
    const { data: courseHistory, refetch: refetchCourseHistory } = useGetCourseHistoryByStudentIdGradeQuery({
        studentId: user?.contactId || '',
        IsRemedial: IsRemedial
    });
    const { data: records, refetch } = useGetCourseHistoryByStudentIdQuery({
        studentId: user?.contactId || '',
    });

    const { data: semesterReg } = useGetSemesterRegistrationQuery({
        studentId: user?.contactId || '',
        registrationType: registrationType || 0,
        academicSession: academicSession || '',
    });

    const [selectedCourses, setSelectedCourses] = useState<Set<string>>(new Set());
    const [insertRecord] = useCreateMutation();
    const [removeRecord] = useDeleteMutation();
    const { getCourseName } = useGetCoursesName();

    const courseType = (courseType?: number) => {
        switch (courseType) {
            case ICourseType.OpenElective:
                return "Open Elective";
            case ICourseType.Elective:
                return "Elective";
            case ICourseType.Major:
                return "Major";
            default:
                return courseType;
        }
    };

    useEffect(() => {
        if (!academicSession || !registrationType || !records) return;

        const defaultSelected = new Set(
            records
                .filter((history) => history.registrationType === Number(registrationType) &&
                    history.academicSession === academicSession).map((item) => item.courseId || '')
        );
        setSelectedCourses(defaultSelected);

        // eslint-disable-next-line
    }, [records, registrationType]);

    const addCourseHistory = async (newCourseHistoryRecord: any) => {
        saveFormData(newCourseHistoryRecord)
            .then(() => {
                refetchCourseHistory();
                refetch()
            })
            .catch(err => {
                const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                setError(message);
                console.error(message);
            });
    };

    const updateCourseHistory = async (courseHistoryId: string) => {
        try {
            removeFormData(courseHistoryId)
                .then(() => {
                    refetchCourseHistory();
                    refetch()
                })
                .catch(err => {
                    const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                    setError(message);
                }).finally(() => {
                    refetchCourseHistory()
                    refetch()
                });
            refetchCourseHistory();
            refetch()
        } catch (error) {
            console.error("Error updating course history:", error);
        }
    };

    const saveFormData = (record: IEntity): Promise<IEntity> => {
        const fields: IFieldValue[] = Object.entries(record)
            .filter(([_, value]) => value !== undefined)
            .map(([field, value]) => ({ field, value, validate: true }));

        return insertRecord({ portalId: siteId || '', userId: user?.contactId, tableId: "mshied_coursehistory", data: fields, triggers: true })
            .unwrap()
            .catch(err => {
                const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                setError(message);
                console.error(message);
                return Promise.reject(message);
            });
    };

    const removeFormData = (courseHistoryId: string): Promise<void> => {
        return removeRecord({ portalId: siteId || '', userId: user?.contactId, sysId: courseHistoryId, tableId: 'mshied_coursehistory', triggers: true })
            .unwrap()
            .catch(err => {
                const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                setError(message);
                return Promise.reject(message);
            });
    };

    const handleCheckboxChange = async (course: ICourseHistory) => {
        if(!semesterReg) return;
        const updatedSelectedCourses = new Set(selectedCourses);

        const courseHistoryRecord = records?.find(
            (history) =>
                history.courseId === course.courseId &&
                history.registrationType === Number(registrationType) &&
                history.academicSession === academicSession
        );

        if (updatedSelectedCourses.has(course.courseId || '')) {
            updatedSelectedCourses.delete(course.courseId || '');
            if (courseHistoryRecord) {
                await updateCourseHistory(courseHistoryRecord.courseHistoryId || '');
            }
        } else {
            updatedSelectedCourses.add(course.courseId || '');
            if (!courseHistoryRecord) {
                const newCourseHistoryRecord = {
                    mshied_name: course.courseId,
                    "slcm_Program@odata.bind": `/mshied_programs(${course.programId})`,
                    "mshied_StudentId@odata.bind": `/contacts(${user?.contactId})`,
                    "edtec365_Semester@odata.bind": `/slcm_semesters(${course.semesterId})`,
                    mshied_creditsattempted: course.credit,
                    edtec365_registrationtype: registrationType,
                    edtec365_semesterregistration: semesterReg[0].registrationId,
                    "mshied_AcademicPeriodid@odata.bind": `/mshied_programrequirements(${academicSession})`,
                    "mshied_CourseId@odata.bind": `/mshied_courses(${course.courseId})`,
                    edtec365_coursetype: course.courseType
                };
                await addCourseHistory(newCourseHistoryRecord);
            }
        }

        setSelectedCourses(updatedSelectedCourses);
        refetchCourseHistory();
        refetch();
    };

    return (
        <div className="container mt-4">
            {courseHistory?.map((item) => (
                <div
                    className="row border rounded p-3 mb-3 align-items-center"
                    key={item.courseHistoryId}
                >
                    <div className="col-2 d-flex justify-content-center">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            disabled={isChecked && disabled}
                            readOnly={isChecked && readOnly}
                            checked={isChecked ? true : selectedCourses.has(item?.courseId || '') || false}
                            onChange={() => handleCheckboxChange(item)}
                        />
                    </div>
                    <div className="col-4">
                        <span>Course</span> <br />
                        {getCourseName(item.courseId || '')}
                    </div>
                    <div className="col-3">
                        <span>Course Type</span> <br />
                        {courseType(item.courseType)}
                    </div>
                    <div className="col-3">
                        <span>Credits</span> <br />
                        {item.credit || 0}
                    </div>
                </div>
            ))}
        </div>
    );
};


Registry.addControl({
    id: "1878ae0c-831c-4cd2-a053-2ed9cb630eec",
    name: "Remedial Registration",
    type: ControlType.RemedialSuppComponent,
    properties: [
        { id: Property.RegistrationType, name: "Registration Type", type: PropertyType.Number },
        { id: Property.IsRemedial, name: "IsRemedial", type: PropertyType.Boolean },
        { id: Property.AcademicSession, name: "AcademicSession", type: PropertyType.String },
        { id: Property.IsChecked, name: "Is Checked", type: PropertyType.Boolean },
        { id: Property.Readonly, name: "ReadOnly", type: PropertyType.Boolean },
        { id: Property.Disabled, name: "Disabled", type: PropertyType.Boolean },
        { id: Property.Tooltip, name: "Tooltip", type: PropertyType.String },
    ],
    component: RemedialRegComponent,
});